import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Save, Lock, CreditCard, ToggleLeft, ToggleRight, CheckCircle } from 'lucide-react';

const PaymentConfigForm = ({ token, API_URL }) => {
    const [configs, setConfigs] = useState([]);
    const [config, setConfig] = useState({
        provider: 'Razorpay',
        apiKey: '',
        apiSecret: '',
        saltIndex: '1',
        environment: 'Sandbox',
        isActive: false
    });
    const [loading, setLoading] = useState(false);
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        fetchConfig();
    }, []);

    const fetchConfig = async () => {
        try {
            const res = await axios.get(`${API_URL}/api/admin/payment-config`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.data && Array.isArray(res.data)) {
                setConfigs(res.data);
                // Try to find the active config first, otherwise default to Razorpay
                const activeConfig = res.data.find(c => c.isActive) || res.data.find(c => c.provider === 'Razorpay') || {
                    provider: 'Razorpay', apiKey: '', apiSecret: '', saltIndex: '1', environment: 'Sandbox', isActive: false
                };
                setConfig(activeConfig);
            }
        } catch (err) {
            console.error("Error fetching payment config", err);
        }
    };

    const handleProviderChange = (e) => {
        const newProvider = e.target.value;
        const currentEnv = config.environment || 'Sandbox';
        const existingConfig = configs.find(c => c.provider === newProvider && c.environment === currentEnv);
        if (existingConfig) {
            setConfig(existingConfig);
        } else {
            setConfig({ provider: newProvider, apiKey: '', apiSecret: '', saltIndex: '1', environment: currentEnv, isActive: false });
        }
    };

    const handleEnvironmentChange = (e) => {
        const newEnv = e.target.value;
        const currentProvider = config.provider;
        const existingConfig = configs.find(c => c.provider === currentProvider && c.environment === newEnv);
        if (existingConfig) {
            setConfig(existingConfig);
        } else {
            setConfig({ provider: currentProvider, apiKey: '', apiSecret: '', saltIndex: '1', environment: newEnv, isActive: false });
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const res = await axios.post(`${API_URL}/api/admin/payment-config`, config, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSaved(true);

            // Update local configs state to reflect the new change
            const updatedConfig = res.data;
            let newConfigs = [...configs];
            const existingIndex = newConfigs.findIndex(c =>
                c.provider === updatedConfig.provider && c.environment === updatedConfig.environment
            );

            if (existingIndex !== -1) {
                newConfigs[existingIndex] = updatedConfig;
            } else {
                newConfigs.push(updatedConfig);
            }

            // Also, if this one became active, deactivate ALL others in local state
            if (updatedConfig.isActive) {
                newConfigs = newConfigs.map(c =>
                    (c.provider === updatedConfig.provider && c.environment === updatedConfig.environment)
                        ? c : { ...c, isActive: false }
                );
            }
            setConfigs(newConfigs);
            setConfig(updatedConfig);
            setTimeout(() => setSaved(false), 3000);
        } catch (err) {
            alert("Failed to save configuration");
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSave} className="space-y-6">
            <div className="flex items-center gap-4 mb-6">
                <button
                    type="button"
                    onClick={() => setConfig({ ...config, isActive: !config.isActive })}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full font-bold transition-all ${config.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}
                >
                    {config.isActive ? <ToggleRight size={24} /> : <ToggleLeft size={24} />}
                    {config.isActive ? 'Payments Enabled' : 'Payments Disabled'}
                </button>
            </div>

            <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Payment Provider</label>
                <select
                    className="w-full border-2 border-gray-200 p-3 rounded-xl focus:border-amber-500 outline-none"
                    value={config.provider}
                    onChange={handleProviderChange}
                >
                    <option value="Razorpay">Razorpay</option>
                    <option value="PhonePe">PhonePe</option>
                    <option value="Paytm">Paytm</option>
                    <option value="Cashfree">Cashfree</option>
                </select>
            </div>

            <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {config.provider === 'Paytm' ? 'Merchant ID (MID)' : config.provider === 'Cashfree' ? 'App ID' : 'API Key / Merchant ID'}
                </label>
                <div className="relative">
                    <CreditCard className="absolute left-3 top-3.5 text-gray-400" size={20} />
                    <input
                        type="text"
                        className="w-full pl-10 border-2 border-gray-200 p-3 rounded-xl focus:border-amber-500 outline-none"
                        value={config.apiKey}
                        onChange={e => setConfig({ ...config, apiKey: e.target.value })}
                        placeholder={config.provider === 'Razorpay' ? 'rzp_test_...' : 'MID / Merchant ID'}
                        required={config.isActive}
                    />
                </div>
            </div>

            <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {config.provider === 'Paytm' ? 'Merchant Key' : config.provider === 'Cashfree' ? 'Secret Key' : 'API Secret / Salt Key'}
                </label>
                <div className="relative">
                    <Lock className="absolute left-3 top-3.5 text-gray-400" size={20} />
                    <input
                        type="password"
                        className="w-full pl-10 border-2 border-gray-200 p-3 rounded-xl focus:border-amber-500 outline-none"
                        value={config.apiSecret}
                        onChange={e => setConfig({ ...config, apiSecret: e.target.value })}
                        placeholder="Secret Key"
                        required={config.isActive}
                    />
                </div>
            </div>

            {(config.provider === 'PhonePe' || config.provider === 'Cashfree') && (
                <>
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Environment</label>
                        <select
                            className="w-full border-2 border-gray-200 p-3 rounded-xl focus:border-amber-500 outline-none mb-4"
                            value={config.environment || 'Sandbox'}
                            onChange={handleEnvironmentChange}
                        >
                            <option value="Sandbox">Sandbox / Testing</option>
                            <option value="Production">Production / Live</option>
                        </select>
                    </div>
                    {config.provider === 'PhonePe' && (
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Salt Index</label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-3.5 text-gray-400" size={20} />
                                <input
                                    type="text"
                                    className="w-full pl-10 border-2 border-gray-200 p-3 rounded-xl focus:border-amber-500 outline-none"
                                    value={config.saltIndex}
                                    onChange={e => setConfig({ ...config, saltIndex: e.target.value })}
                                    placeholder="1"
                                    required={config.isActive}
                                />
                            </div>
                        </div>
                    )}
                </>
            )}

            <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-amber-600 to-amber-700 text-white py-3 rounded-xl font-bold hover:shadow-lg transition-all flex justify-center items-center gap-2"
            >
                {loading ? 'Saving...' : saved ? <><CheckCircle size={20} /> Saved!</> : <><Save size={20} /> Save Configuration</>}
            </button>
        </form>
    );
};

export default PaymentConfigForm;
